import { useContext, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { type OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import { cn } from '@/lib/utils';
import { DialogScrollContext } from '@scio3/ui';
import { ScrollContainer } from './ScrollContainer';

interface DialogScrollBodyProps {
  children: ReactNode;
  className?: string;
}

/**
 * Scrollable body area for dialogs.
 *
 * - Uses OverlayScrollbars (auto-hide, no track — same style as side nav panels)
 * - Scrollbar renders in the dialog's right padding area (not overlapping content)
 * - Focus rings on form elements aren't clipped by the scroll viewport
 * - Reports scroll state to DialogScrollContext so DialogFooter shows an upward
 *   shadow when there's more content below
 *
 * Usage: Replace `<div className="flex-1 overflow-y-auto">` inside a flex-col DialogContent.
 */
export function DialogScrollBody({ children, className }: DialogScrollBodyProps) {
  const osRef = useRef<OverlayScrollbarsComponentRef>(null);
  const { setCanScrollDown } = useContext(DialogScrollContext);

  const checkScroll = useCallback(() => {
    const instance = osRef.current?.osInstance();
    if (!instance) return;
    const viewport = instance.elements().viewport;
    const threshold = 4;
    const hasMore = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight > threshold;
    setCanScrollDown(hasMore);
  }, [setCanScrollDown]);

  useEffect(() => {
    const instance = osRef.current?.osInstance();
    if (!instance) return;

    const viewport = instance.elements().viewport;
    viewport.addEventListener('scroll', checkScroll, { passive: true });
    // Check initially and on resize
    checkScroll();
    const ro = new ResizeObserver(checkScroll);
    ro.observe(viewport);

    return () => {
      viewport.removeEventListener('scroll', checkScroll);
      ro.disconnect();
      setCanScrollDown(false);
    };
  }, [checkScroll, setCanScrollDown]);

  return (
    // fabGutter={false} is redundant with `useInsideOverlay()` (DialogContent already
    // marks its subtree as an overlay) — kept explicit so this stays correct if this body
    // is ever rendered outside a DialogContent, and so the intent is readable here.
    <ScrollContainer ref={osRef} fabGutter={false} className="-ml-3 -mr-6">
      <div className={cn('pt-1 pl-3 pr-6 pb-3', className)}>{children}</div>
    </ScrollContainer>
  );
}
